import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

export type QueryRunPhase = 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED';

export interface QueryRunStateProps {
  readonly phase: QueryRunPhase;
  /** Seconds since the console started the query (Q-1's "Läuft seit n s"). */
  readonly elapsedSeconds: number;
  /** `q_7f3a…` — shown while running, for an operator's question. */
  readonly queryId?: string;
  /** SUCCEEDED with no rows: "Keine Daten". */
  readonly empty?: boolean;
  /** FAILED / CANCELLED: the translated title and hint of the code, and the engine's own message (Q-8). */
  readonly errorCode?: string;
  readonly errorTitle?: string;
  readonly errorHint?: string;
  readonly engineMessage?: string;
  /** TOO_MANY_GROUPS is a warning the author can act on, not a failure of the warehouse (P-5). */
  readonly warning?: boolean;
  readonly stats?: {
    readonly engineMs?: number;
    readonly scannedBytes?: number;
    readonly reused?: boolean;
  };
  /** "Ergebnis vom …" — already formatted. */
  readonly resultAt?: string;
  readonly onCancel?: () => void;
  readonly onRerun?: () => void;
  /** A third action for a failed state: "Zum Feld", "Filter hinzufügen". */
  readonly action?: { readonly label: string; readonly onClick: () => void };
}

function bytes(n: number, locale: string): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: unit === 0 ? 0 : 1 }).format(value)} ${units[unit] ?? 'B'}`;
}

/**
 * The run state of a query (SDD-024 Q-1, Q-8; mockup boards 3 and 10): a chip that names the state
 * — "Läuft seit n s", "Aktuell", "Keine Daten", "Fehlgeschlagen", "Abgebrochen" — and, beside it,
 * what the reader can do: cancel a running query, run again bypassing the result reuse, or act on a
 * refusal. The page stays usable while a query runs; nothing here blocks.
 */
export function QueryRunState(props: QueryRunStateProps): ReactNode {
  const { t, i18n } = useTranslation('patterns');
  const locale = i18n.language.startsWith('en') ? 'en-GB' : 'de-DE';
  const { phase, elapsedSeconds, stats } = props;
  const running = phase === 'QUEUED' || phase === 'RUNNING';
  const chipLabel = running
    ? t('reporting.run.running', { seconds: elapsedSeconds })
    : phase === 'SUCCEEDED'
      ? props.empty === true
        ? t('reporting.run.noData')
        : t('reporting.run.succeeded')
      : phase === 'FAILED'
        ? t('reporting.run.failed')
        : t('reporting.run.cancelled');
  const color = running
    ? 'info'
    : phase === 'SUCCEEDED'
      ? 'success'
      : props.warning === true
        ? 'warning'
        : 'error';

  return (
    <Stack spacing={1} data-testid="query-run-state" data-phase={phase}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip
          size="small"
          color={color}
          variant={phase === 'SUCCEEDED' ? 'outlined' : 'filled'}
          label={chipLabel}
          role="status"
          aria-label={`${t('reporting.run.state')}: ${chipLabel}`}
          data-testid="run-state-chip"
        />
        {running && props.queryId !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {t('reporting.run.queryId', { id: props.queryId })}
          </Typography>
        )}
        {phase === 'SUCCEEDED' && props.resultAt !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {t('reporting.run.resultOf', { at: props.resultAt })}
          </Typography>
        )}
        {phase === 'SUCCEEDED' && stats?.engineMs !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {t('reporting.run.stats', {
              seconds: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
                stats.engineMs / 1000,
              ),
              bytes: bytes(stats.scannedBytes ?? 0, locale),
            })}
            {stats.reused === true && ` · ${t('reporting.run.reused')}`}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {running && props.onCancel !== undefined && (
          <Button size="small" onClick={props.onCancel}>
            {t('reporting.run.cancel')}
          </Button>
        )}
        {!running && props.onRerun !== undefined && (
          <Button size="small" onClick={props.onRerun}>
            {t('reporting.run.rerun')}
          </Button>
        )}
      </Stack>
      {running && (
        <Box>
          <LinearProgress aria-label={chipLabel} />
          {elapsedSeconds >= 10 && (
            <Typography variant="caption" color="text.secondary">
              {t('reporting.run.stillWorking')}
            </Typography>
          )}
        </Box>
      )}
      {(phase === 'FAILED' || phase === 'CANCELLED') && props.errorTitle !== undefined && (
        <Alert
          severity={props.warning === true ? 'warning' : 'error'}
          action={
            props.action === undefined ? undefined : (
              <Button size="small" color="inherit" onClick={props.action.onClick}>
                {props.action.label}
              </Button>
            )
          }
        >
          <AlertTitle>{props.errorTitle}</AlertTitle>
          {props.engineMessage !== undefined && props.engineMessage !== '' && (
            <Box
              component="code"
              sx={{ display: 'block', whiteSpace: 'pre-wrap', mb: 0.5 }}
              data-error-code={props.errorCode}
            >
              {props.engineMessage}
            </Box>
          )}
          {props.errorHint}
        </Alert>
      )}
    </Stack>
  );
}
